
import React from "react";
import { Briefcase, ShoppingBag, Phone, Wrench, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";

const ProductSuite = () => {
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Navbar />
      <main className="flex-1 relative">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50"></div>
          <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-violet-100/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-indigo-100/20 to-transparent"></div>
        </div>

        <section className="py-20">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-rainbow font-poppins">
                Our Product Suite
              </h1>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto font-nunito">
                Discover our comprehensive range of AI solutions designed to transform retail and e-commerce businesses of all sizes.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <ProductCard 
                title="Atelier" 
                description="Premium enterprise AI solution for retail businesses with custom brand voices and multichannel support."
                icon={<Briefcase size={32} />}
                color="from-indigo-500 to-blue-600"
                textColor="text-indigo-600"
                bgColor="bg-indigo-50"
                borderColor="border-indigo-200"
              />
              
              <ProductCard 
                title="Pronto" 
                description="Quick-to-deploy AI solution for Shopify stores. Install in minutes with zero technical setup."
                icon={<ShoppingBag size={32} />}
                color="from-pink-500 to-rose-600"
                textColor="text-pink-600"
                bgColor="bg-pink-50"
                borderColor="border-pink-200"
              />
              
              <ProductCard 
                title="Concierge" 
                description="AI phone receptionist that handles calls, schedules appointments, and provides service 24/7."
                icon={<Phone size={32} />}
                color="from-cyan-500 to-teal-600" 
                textColor="text-cyan-600"
                bgColor="bg-cyan-50"
                borderColor="border-cyan-200"
              />
              
              <ProductCard 
                title="Custom AI Builds" 
                description="Bespoke AI solutions tailored to specific industry needs and unique business requirements."
                icon={<Wrench size={32} />} 
                color="from-amber-500 to-orange-600"
                textColor="text-amber-600"
                bgColor="bg-amber-50"
                borderColor="border-amber-200"
              />
            </div>

            <div className="mt-16 text-center">
              <Link to="/pitch-deck">
                <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 font-nunito text-white py-6 px-8 text-lg">
                  View Full Pitch Deck
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

interface ProductCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  textColor: string;
  bgColor: string;
  borderColor: string;
}

const ProductCard = ({ title, description, icon, color, textColor, bgColor, borderColor }: ProductCardProps) => {
  return (
    <div className={`bg-white backdrop-blur-sm ${borderColor} border p-8 rounded-lg hover:shadow-lg transition-all h-full shadow-md`}>
      <div className={`bg-gradient-to-r ${color} p-4 rounded-full inline-flex mb-6`}>
        <div className="text-white">
          {icon}
        </div>
      </div>
      <h3 className={`text-2xl font-bold mb-4 ${textColor} font-poppins`}>{title}</h3>
      <p className="text-gray-700 mb-6 font-nunito">{description}</p>
      <Link to="/pitch-deck" className="inline-flex items-center text-violet-600 hover:text-violet-800 font-medium transition-colors font-nunito">
        Learn more
        <ChevronRight className="ml-1 h-4 w-4" />
      </Link>
    </div>
  );
};

export default ProductSuite;
